import React, { FunctionComponent } from "react";

import { bemClassNames, invariant } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { ButtonBackgroundColor } from "../ButtonProvider";
import { Icon, IconColor } from "../Icon";
import { Layout } from "../Layout";
import { TextColor } from "../Text";
import { TextButton } from "../TextButton";
import { ComponentSize } from "../types";

export type WizardControlPanelProps = {
    /** Hodnota aktivního indexu */
    readonly activeIndex?: number;
    /** Příznak určující, zda jsou na stránce splněny všechny podmínky pro pokračování dále */
    readonly canProceed?: boolean;
    /** Počet stránek */
    readonly numberOfPages: number;
    /** Text pro tlačítko Zpět */
    readonly backButtonText: string;
    /** Text pro tlačítko Další */
    readonly nextButtonText: string;
    /** Text pro tlačítko Dokončit */
    readonly finishButtonText: string;
    /** Vlastní ikona progressu */
    readonly progressIcon?: IconCode;
    /** Callback funkce volána při kliku na tlačítko Další */
    readonly onNextButtonClick: () => void;
    /** Callback funkce volána při kliku na tlačítko Zpět */
    readonly onBackButtonClick: () => void;
    /** Callback funkce volána při kliku na tlačítko, které dokončuje průvodce */
    readonly onFinishButtonClick: () => void;
};

const classNames = bemClassNames("wizard-control-panel");
const wizardControlPanelClassName = classNames();
const backButtonClassName = classNames("back-button");
const nextButtonClassName = classNames("next-button");
const iconLayoutClassName = classNames("icon-layout");

export const WizardControlPanel: FunctionComponent<WizardControlPanelProps> = ({
    activeIndex = 0,
    canProceed = true,
    numberOfPages,
    onNextButtonClick,
    onBackButtonClick,
    onFinishButtonClick,
    progressIcon,
    backButtonText,
    nextButtonText,
    finishButtonText,
}) => {
    invariant(numberOfPages > 0, "You have to have at least one page.");
    invariant(activeIndex >= 0 && activeIndex <= numberOfPages - 1, "Active index is smaller than 0 or greater than number of pages.");

    return (
        <div className={wizardControlPanelClassName}>
            <div className={backButtonClassName}>
                <TextButton qa={"backButton"} size={ComponentSize.large} color={TextColor.colorPrimary} title={backButtonText} text={backButtonText} disabled={activeIndex === 0} onClick={onBackButtonClick} />
            </div>
            <Layout>
                {Array.from({ length: numberOfPages }, (_, i: number) => (
                    <span key={i} className={iconLayoutClassName}>
                        <Icon iconCode={progressIcon ?? IconCode.uiDotLargeFilled} color={activeIndex === i ? IconColor.colorPrimary : IconColor.grey1} />
                    </span>
                ))}
            </Layout>
            <div className={nextButtonClassName}>
                {activeIndex === numberOfPages - 1 ? (
                    <TextButton qa={"finishButton"} disabled={!canProceed} backgroundColor={ButtonBackgroundColor.buttonPrimary} color={TextColor.whiteBlack} title={finishButtonText} text={finishButtonText} size={ComponentSize.large} onClick={onFinishButtonClick} />
                ) : (
                    <TextButton qa={"nextButton"} size={ComponentSize.large} color={TextColor.colorPrimary} disabled={!canProceed} title={nextButtonText} text={nextButtonText} onClick={onNextButtonClick} />
                )}
            </div>
        </div>
    );
};
